<template>
  <el-dialog
    v-model="visible"
    title="库位确认 / 数量核查 · 责任人"
    width="720px"
    destroy-on-close
    @open="onOpen"
  >
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="tip"
      title="说明"
      description="按人勾选谁会收到任务。仍为共享完成：任一人完成后，其他人同来源的待办会同步关闭。清空某类名单后该类将不再派发新任务。"
    />

    <el-tabs v-model="activeTab">
      <el-tab-pane
        v-for="t in tasks"
        :key="t.key"
        :label="`${t.label}（${selectedCount(t.key)}）`"
        :name="t.key"
      >
        <p class="hint">{{ t.hint }}</p>
        <div class="section-head">
          <el-input
            v-model="filterKeyword"
            placeholder="筛选姓名 / 用户名 / 部门 / 职务"
            clearable
            style="width: 260px"
          />
          <div class="toolbar">
            <el-button link type="primary" @click="selectAllFiltered(t.key)">全选当前</el-button>
            <el-button link @click="applyDefaults(t.key)">恢复职务默认</el-button>
            <el-button link @click="clearTask(t.key)">清空</el-button>
          </div>
        </div>
        <div v-loading="loading" class="user-list">
          <div v-if="filteredUsers.length">
            <div v-for="u in filteredUsers" :key="`${t.key}-${u.user_id}`" class="user-row">
              <el-checkbox
                :model-value="isSelected(t.key, u.user_id)"
                @change="(v) => toggleUser(t.key, u.user_id, v)"
              >
                {{ u.full_name || u.username }}
                <span class="user-meta">
                  {{ u.username
                  }}{{ u.position ? ` · ${u.position}` : ''
                  }}{{ u.department ? ` · ${u.department}` : '' }}
                </span>
              </el-checkbox>
            </div>
          </div>
          <el-empty v-else-if="!loading" description="暂无可用用户" />
        </div>
      </el-tab-pane>
    </el-tabs>

    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  getSharedTaskAssignees,
  saveSharedTaskAssignees
} from '@/api/taskEvents'

const visible = defineModel({ type: Boolean, default: false })

const loading = ref(false)
const saving = ref(false)
const filterKeyword = ref('')
const activeTab = ref('inventory_location_confirm')
const users = ref([])
const tasks = ref([])
/** task_key -> { [user_id]: true } */
const selectedByTask = reactive({})

function ensureMap(key) {
  if (!selectedByTask[key]) selectedByTask[key] = {}
  return selectedByTask[key]
}

function selectedCount(key) {
  return Object.keys(ensureMap(key)).filter((id) => ensureMap(key)[id]).length
}

function isSelected(key, userId) {
  return !!ensureMap(key)[Number(userId)]
}

function toggleUser(key, userId, checked) {
  const map = ensureMap(key)
  const id = Number(userId)
  if (checked) map[id] = true
  else delete map[id]
}

const filteredUsers = computed(() => {
  const kw = (filterKeyword.value || '').trim().toLowerCase()
  if (!kw) return users.value
  return users.value.filter((u) => {
    const blob = [u.full_name, u.username, u.department, u.position]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return blob.includes(kw)
  })
})

function selectAllFiltered(key) {
  const map = ensureMap(key)
  filteredUsers.value.forEach((u) => {
    map[Number(u.user_id)] = true
  })
}

function clearTask(key) {
  selectedByTask[key] = {}
}

function applyDefaults(key) {
  const t = tasks.value.find((x) => x.key === key)
  const map = {}
  ;(t?.default_user_ids || []).forEach((id) => {
    map[Number(id)] = true
  })
  selectedByTask[key] = map
}

async function onOpen() {
  loading.value = true
  filterKeyword.value = ''
  try {
    const res = await getSharedTaskAssignees()
    const data = res?.data || res || {}
    users.value = Array.isArray(data.users) ? data.users : []
    tasks.value = Array.isArray(data.tasks) ? data.tasks : []
    Object.keys(selectedByTask).forEach((k) => delete selectedByTask[k])
    for (const t of tasks.value) {
      const map = {}
      ;(t.user_ids || []).forEach((id) => {
        map[Number(id)] = true
      })
      selectedByTask[t.key] = map
    }
    if (tasks.value.length) {
      activeTab.value = tasks.value[0].key
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '加载失败')
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  try {
    const payload = {
      tasks: tasks.value.map((t) => ({
        key: t.key,
        user_ids: Object.keys(ensureMap(t.key))
          .filter((id) => ensureMap(t.key)[id])
          .map((id) => Number(id))
      }))
    }
    const res = await saveSharedTaskAssignees(payload)
    if (res?.success !== false) {
      ElMessage.success(res?.message || '已保存')
      visible.value = false
    } else {
      ElMessage.error(res?.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.tip {
  margin-bottom: 14px;
}
.hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.user-list {
  max-height: 420px;
  overflow: auto;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  padding: 8px 12px;
}
.user-row {
  padding: 8px 0;
  border-bottom: 1px solid var(--el-border-color-extra-light);
}
.user-row:last-child {
  border-bottom: none;
}
.user-meta {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin-left: 6px;
}
</style>
